import { useState, useEffect } from 'react';
import { Table, Card, Space, Input, Button, Modal, Tag, Typography, Spin } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  MessageOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';

const { Text } = Typography;

interface ChatConversation {
  tripId: number;
  tripType: string;
  clientId: number;
  clientName: string;
  clientPhone: string;
  driverId: number;
  driverName: string;
  driverPhone: string;
  messageCount: number;
  lastMessageAt: string;
}

interface ChatMessage {
  id: number;
  tripId: number;
  senderId: number;
  senderRole: 'CLIENT' | 'DRIVER';
  senderName: string;
  content: string;
  createdAt: string;
}

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida',
  INTERREGION_TAXI: 'Shaharlararo',
  SHAHARLAR_ARO: 'Viloyat ichida',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk',
  YOL_YOLAKAY: 'Savdolashish',
};

const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'blue',
  INTERREGION_TAXI: 'purple',
  SHAHARLAR_ARO: 'magenta',
  POSTAGE: 'orange',
  CARGO: 'cyan',
  YOL_YOLAKAY: 'green',
};

export const ChatViewer = () => {
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/chat/conversations', {
        params: {
          search: search || undefined,
          page,
          limit: pageSize,
        },
      });
      setConversations(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch {
      setConversations([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [page, pageSize]);

  const handleViewMessages = async (conversation: ChatConversation) => {
    setSelectedConversation(conversation);
    setModalOpen(true);
    setMessagesLoading(true);
    try {
      const response = await apiClient.get('/admin/chat/messages', {
        params: {
          tripId: conversation.tripId,
          tripType: conversation.tripType,
        },
      });
      setMessages(response.data?.data || response.data || []);
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const columns = [
    {
      title: 'Sayohat ID',
      dataIndex: 'tripId',
      key: 'tripId',
      width: 100,
      render: (id: number) => <Text strong>#{id}</Text>,
    },
    {
      title: 'Sayohat turi',
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag color={tripTypeColors[type] || 'default'}>
          {tripTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Mijoz',
      key: 'client',
      render: (_: unknown, record: ChatConversation) => (
        <div>
          <div>{record.clientName || 'Ismi yo\'q'}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.clientPhone}</div>
        </div>
      ),
    },
    {
      title: 'Haydovchi',
      key: 'driver',
      render: (_: unknown, record: ChatConversation) => (
        <div>
          <div>{record.driverName || 'Ismi yo\'q'}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.driverPhone}</div>
        </div>
      ),
    },
    {
      title: 'Xabarlar',
      dataIndex: 'messageCount',
      key: 'messageCount',
      width: 100,
      render: (count: number) => (
        <Space>
          <MessageOutlined />
          <span>{count}</span>
        </Space>
      ),
    },
    {
      title: 'Oxirgi xabar',
      dataIndex: 'lastMessageAt',
      key: 'lastMessageAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: ChatConversation) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewMessages(record)}
          title="Ko'rish"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <MessageOutlined style={{ marginRight: 8 }} />
        Chat ko'ruvchi
      </h2>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Sayohat ID yoki foydalanuvchi ismi"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            allowClear
            onPressEnter={fetchConversations}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchConversations}>
            Qidirish
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={conversations}
          columns={columns}
          rowKey={(record) => `${record.tripType}-${record.tripId}`}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
        />
      </Card>

      {/* Chat Messages Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>
              Chat - Sayohat #{selectedConversation?.tripId}
              {selectedConversation?.tripType && (
                <Tag
                  color={tripTypeColors[selectedConversation.tripType] || 'default'}
                  style={{ marginLeft: 8 }}
                >
                  {tripTypeLabels[selectedConversation.tripType] || selectedConversation.tripType}
                </Tag>
              )}
            </span>
          </Space>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedConversation(null);
          setMessages([]);
        }}
        footer={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Faqat ko'rish rejimi - xabar yuborish mumkin emas
          </Text>
        }
        width={700}
      >
        {selectedConversation && (
          <div style={{ marginBottom: 16 }}>
            <Space size="large">
              <div>
                <Text type="secondary">Mijoz: </Text>
                <Text strong>{selectedConversation.clientName}</Text>
              </div>
              <div>
                <Text type="secondary">Haydovchi: </Text>
                <Text strong>{selectedConversation.driverName}</Text>
              </div>
            </Space>
          </div>
        )}

        {messagesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#7F8C8D' }}>
            Xabarlar topilmadi
          </div>
        ) : (
          <div
            style={{
              maxHeight: 500,
              overflowY: 'auto',
              padding: '16px 8px',
              backgroundColor: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            {messages.map((msg) => {
              const isClient = msg.senderRole === 'CLIENT';
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    justifyContent: isClient ? 'flex-start' : 'flex-end',
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      maxWidth: '70%',
                      padding: '10px 14px',
                      borderRadius: isClient ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                      backgroundColor: isClient ? '#ffffff' : '#1890ff',
                      color: isClient ? '#000000' : '#ffffff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div style={{ fontSize: 11, marginBottom: 4, opacity: 0.7 }}>
                      <UserOutlined style={{ marginRight: 4 }} />
                      {msg.senderName || (isClient ? 'Mijoz' : 'Haydovchi')}
                    </div>
                    <div style={{ wordBreak: 'break-word' }}>{msg.content}</div>
                    <div
                      style={{
                        fontSize: 10,
                        textAlign: 'right',
                        marginTop: 4,
                        opacity: 0.6,
                      }}
                    >
                      {dayjs(msg.createdAt).format('HH:mm')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};
